import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Zap, Target, AlertCircle, ChevronDown, ChevronUp, Heart, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react'
import { usePulse, usePrefetch } from '../hooks/useGames'

// ============================================================================
// SEGMENTED CONTROL - Pulse tabs: Signals (edges + line moves) / Feed
// ============================================================================
const SEGMENTS = [
  { id: 'feed', label: 'Feed', icon: AlertCircle, dotColor: 'bg-orange-500' },
  { id: 'signals', label: 'Signals', icon: Target, dotColor: 'bg-emerald-500' },
]

function SegmentedControl({ activeSegment, onSegmentChange, counts }) {
  const [touchStart, setTouchStart] = useState(null)
  
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX)
  }
  
  const handleTouchEnd = (e) => {
    if (!touchStart) return
    const touchEnd = e.changedTouches[0].clientX
    const diff = touchStart - touchEnd
    const currentIndex = SEGMENTS.findIndex(s => s.id === activeSegment)
    
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < SEGMENTS.length - 1) {
        onSegmentChange(SEGMENTS[currentIndex + 1].id)
      } else if (diff < 0 && currentIndex > 0) {
        onSegmentChange(SEGMENTS[currentIndex - 1].id)
      }
    }
    setTouchStart(null)
  }
  
  return (
    <div 
      className="px-3 pb-1"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Section tabs (nested under Pulse) - underline style, NOT the NBA/NFL scope toggle */}
      <div className="flex justify-center">
        <div className="inline-flex border-b border-gray-200">
          {SEGMENTS.map((segment) => {
            const isActive = activeSegment === segment.id

            return (
              <button
                key={segment.id}
                onClick={() => onSegmentChange(segment.id)}
                className="transition-all duration-150"
                style={{
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: isActive ? '#FF4D6D' : '#9CA3AF',
                  borderBottom: isActive ? '2px solid #FF4D6D' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                {segment.label}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}


// ============================================================================
// PRIORITY CARD - Clean card with proper spacing
// ============================================================================
function PriorityCard({ title, icon: Icon, color, items, onSeeAll, onItemClick, renderItem }) {
  if (!items || items.length === 0) return null
  
  const colorClasses = {
    emerald: 'border-emerald-100',
    orange: 'border-orange-100',
    blue: 'border-blue-100',
    red: 'border-red-100',
  }
  
  const iconClasses = {
    emerald: 'text-emerald-500',
    orange: 'text-orange-500',
    blue: 'text-blue-500',
    red: 'text-red-500',
  }
  
  return (
    <div className={`mx-4 mb-3 rounded-xl border bg-white ${colorClasses[color] || colorClasses.blue}`}>
      {/* Card Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon size={14} className={iconClasses[color] || iconClasses.blue} />
          <span className="text-xs font-semibold text-gray-900">{title}</span>
          <span className="text-[9px] bg-white/60 text-gray-600 px-1.5 py-0.5 rounded-full">
            {items.length}
          </span>
        </div>
        <button 
          onClick={onSeeAll}
          className="flex items-center gap-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-700"
        >
          See all <ChevronRight size={12} />
        </button>
      </div>
      
      {/* Card Content - Show top 3 items */}
      <div className="divide-y divide-gray-50">
        {items.slice(0, 3).map((item, idx) => (
          <div key={item.id || idx} onClick={() => onItemClick?.(item)} className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition">
            {renderItem(item)}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// SPORT FILTER - NBA / NFL (no All - each sport has its own updates)
// ============================================================================
const SPORTS = [
  { id: 'nba', label: 'NBA', active: true },
  { id: 'nfl', label: 'NFL', active: true },
]

function SportFilter({ activeSport, onSportChange }) {
  return (
    <div className="flex items-center gap-3 px-3 py-1">
      {SPORTS.map(sport => {
        const isActive = activeSport === sport.id
        const isDisabled = !sport.active
        return (
          <button
            key={sport.id}
            onClick={() => !isDisabled && onSportChange(sport.id)}
            disabled={isDisabled}
            className="transition-all duration-150 relative"
            style={{
              fontSize: '12px',
              fontWeight: isActive ? '600' : '500',
              color: isDisabled ? '#D1D5DB' : isActive ? '#1F2937' : '#9CA3AF',
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            {sport.label}
          </button>
        )
      })}
    </div>
  )
}

// ============================================================================
// GAME FILTER - Filter live feed by specific game
// ============================================================================
function GameFilter({ games, selectedGame, onGameChange }) {
  // Only show filter if we have games with valid team data
  const getAbbr = (team) =>
    team?.abbreviation || team?.code || team?.short_name || (team?.name ? team.name.slice(0, 3).toUpperCase() : null)

  const validGames = (games || []).filter(g => {
    const home = g.home_team || g.homeTeam || g.teams?.[0]
    const away = g.visitor_team || g.away_team || g.awayTeam || g.teams?.[1]
    return getAbbr(home) && getAbbr(away)
  })
  
  // Hide filter entirely if no valid games
  if (validGames.length === 0) return null
  
  return (
    <div className="px-4 py-2 border-b border-gray-100">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => onGameChange(null)}
          className={`flex-shrink-0 px-3 py-1 text-[11px] font-medium rounded-full transition-all ${
            !selectedGame 
              ? 'bg-gray-900 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {validGames.map(game => {
          const isSelected = selectedGame === String(game.id)
          const statusText = (game.status || '').toString().toLowerCase()
          const timeText = (game.time || '').toString().toLowerCase()
          const isLive = statusText.includes('live') || statusText.includes('q') || timeText.includes('q')
          const home = game.home_team || game.homeTeam || game.teams?.[0]
          const away = game.visitor_team || game.away_team || game.awayTeam || game.teams?.[1]
          return (
            <button
              key={game.id}
              onClick={() => onGameChange(String(game.id))}
              className={`flex-shrink-0 px-3 py-1 text-[11px] font-medium rounded-full transition-all ${
                isSelected 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {getAbbr(away)} @ {getAbbr(home)}
              {isLive && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// MAIN PULSE COMPONENT
// ============================================================================
export default function PulseTab({
  games = [],
  onAskAI,
  selectedLeague,
  onLeagueChange,
  mode = 'pulse_live',
}) {
  const { prefetchPulse } = usePrefetch()
  const { data: pulseData } = usePulse(selectedLeague || 'nba')
  const pulseItems = pulseData?.items || []
  
  // Prefetch the other league's pulse data on mount for faster switching
  useEffect(() => {
    const otherLeague = (selectedLeague || 'nba') === 'nba' ? 'nfl' : 'nba'
    prefetchPulse(otherLeague)
  }, []) // Only on mount
  
  // Sport filter state - use prop if provided, otherwise default to NBA
  const [localSport, setLocalSport] = useState('nba')
  const activeSport = selectedLeague || localSport
  const setActiveSport = onLeagueChange || setLocalSport
  
  // Game filter for live feed
  const [selectedGame, setSelectedGame] = useState(null)
  
  // Persist active segment - default to signals (most actionable)
  const [activeSegment, setActiveSegment] = useState('signals')
  
  const handleSegmentChange = useCallback((segment) => {
    setActiveSegment(segment)
    if (typeof window !== 'undefined') {
      localStorage.setItem('pulse_segment', segment)
    }
  }, [])
  
  // Group items by type (injuries includes status_change for Updates tab)
  // Filter by active sport if items have a league property
  const { liveEvents, edges, lineMovements, injuries, news } = useMemo(() => {
    const sportFilter = (item) => {
      // If item has no league, assume NBA (legacy data)
      const itemLeague = (item.league || 'nba').toLowerCase()
      return itemLeague === activeSport
    }
    
    const live = pulseItems.filter(i => i.type === 'live_event' && sportFilter(i)).slice(0, 25)
    const edg = pulseItems.filter(i => i.type === 'edge' && sportFilter(i)).slice(0, 10)
    
    // Normalize line_move items to consistent format for rendering
    const lineMove = pulseItems
      .filter(i => i.type === 'line_move' && sportFilter(i))
      .slice(0, 10)
      .map(item => {
        // If already has normalized structure, use it
        if (item.matchup && item.delta) return item
        
        // Normalize from API format (metadata.spreadOpen, metadata.spreadCurrent)
        const meta = item.metadata || {}
        const spreadOpen = parseFloat(meta.spreadOpen) || 0
        const spreadCurrent = parseFloat(meta.spreadCurrent) || 0
        const spreadDelta = spreadCurrent - spreadOpen
        
        return {
          ...item,
          matchup: meta.matchup || item.title || 'Unknown',
          gameId: meta.gameId || item.gameId,
          delta: { spread: spreadDelta, total: 0 },
          opening: { spread: spreadOpen },
          current: { spread: spreadCurrent },
          openingAt: meta.openingAt,
          currentAt: meta.currentAt,
          books: meta.books || []
        }
      })
    // Include both injuries AND status changes for the Updates tab
    const inj = pulseItems.filter(i => (i.type === 'injury' || i.type === 'status_change') && sportFilter(i)).slice(0, 20)
    const newsItems = pulseItems.filter(i => i.type === 'news' && sportFilter(i)).slice(0, 10)
    return { liveEvents: live, edges: edg, lineMovements: lineMove, injuries: inj, news: newsItems }
  }, [pulseItems, activeSport])

  const getTeamAbbr = useCallback((team) => {
    if (!team) return ''
    return (
      team.abbreviation ||
      team.code ||
      team.short_name ||
      (team.name ? team.name.slice(0, 3).toUpperCase() : '')
    )
  }, [])

  const getGameTeams = useCallback((game) => {
    if (!game) return { home: null, away: null }
    if (game.home_team || game.visitor_team || game.away_team) {
      return {
        home: game.home_team || game.homeTeam || null,
        away: game.visitor_team || game.away_team || game.awayTeam || null,
      }
    }
    if (Array.isArray(game.teams)) {
      return { home: game.teams[0] || null, away: game.teams[1] || null }
    }
    return { home: null, away: null }
  }, [])

  const getGameScores = useCallback((game) => {
    if (!game) return { homeScore: null, awayScore: null }
    if (game.home_team_score != null || game.visitor_team_score != null) {
      return { homeScore: game.home_team_score ?? null, awayScore: game.visitor_team_score ?? null }
    }
    if (Array.isArray(game.teams)) {
      return {
        homeScore: game.teams[0]?.score ?? game.teams[0]?.points ?? null,
        awayScore: game.teams[1]?.score ?? game.teams[1]?.points ?? null,
      }
    }
    return { homeScore: null, awayScore: null }
  }, [])

  const isLiveGame = useCallback((game) => {
    const statusText = (game?.status || '').toString().toLowerCase()
    const timeText = (game?.time || '').toString().toLowerCase()
    if (statusText.includes('final')) return false
    return statusText.includes('live') || statusText.includes('q') || timeText.includes('q')
  }, [])

  // Live games with scores
  const liveGames = useMemo(() => 
    games.filter(g => isLiveGame(g))
  , [games, isLiveGame])
  
  // Clutch games (Q4, within 5 pts)
  const clutchGames = useMemo(() => 
    liveGames.filter(g => {
      const diff = Math.abs((g.home_team_score || 0) - (g.visitor_team_score || 0))
      return g.status?.includes('Q4') && diff <= 5
    })
  , [liveGames])

  // Build live feed from backend events + frontend-generated
  const liveFeed = useMemo(() => {
    const feed = []
    
    // Backend events - use canonical format (title/detail) with fallback to legacy (text/subtext)
    liveEvents.forEach(event => {
      feed.push({
        id: event.id,
        type: event.type,
        icon: '',
        title: event.title || event.text || 'Update',
        subtitle: event.detail || event.description || event.subtext || '',
        action: event.action,
        priority: event.priority || 2,
        gameId: event.gameId ? String(event.gameId) : undefined,
        source: 'backend',
        timestamp: event.timestamp,
        metadata: event.metadata || event.meta || null,
        league: event.league,
      })
    })
    
    // Frontend-generated from live games
    liveGames.forEach(game => {
      const { homeScore, awayScore } = getGameScores(game)
      const safeHomeScore = homeScore ?? 0
      const safeAwayScore = awayScore ?? 0
      const diff = Math.abs(safeHomeScore - safeAwayScore)
      const { home, away } = getGameTeams(game)
      const homeAbbr = getTeamAbbr(home)
      const awayAbbr = getTeamAbbr(away)
      const league = (game.league || game.raw?.league || activeSport || 'nba').toString().toLowerCase()
      
      if (diff <= 5 && (game.status || '').toString().toUpperCase().includes('Q4')) {
        feed.push({
          id: `close-${game.id}`,
          type: 'close_game',
          icon: '',
          title: `Clutch: ${awayAbbr} ${safeAwayScore} - ${safeHomeScore} ${homeAbbr}`,
          subtitle: game.status || game.time || '',
          priority: 1,
          source: 'frontend',
          gameId: String(game.id),
          league,
        })
      }
      
      if (diff >= 20) {
        const leader = safeHomeScore > safeAwayScore ? homeAbbr : awayAbbr
        feed.push({
          id: `blowout-${game.id}`,
          type: 'blowout',
          icon: '',
          title: `${leader} up ${diff}`,
          subtitle: `${awayAbbr} @ ${homeAbbr}`,
          priority: 3,
          source: 'frontend',
          gameId: String(game.id),
          league,
        })
      }
    })
    
    // Filter by selected game if set
    const filtered = selectedGame 
      ? feed.filter(item => item.gameId === selectedGame)
      : feed
    
    return filtered.sort((a, b) => {
      if (a.timestamp && b.timestamp) return new Date(b.timestamp) - new Date(a.timestamp)
      return a.priority - b.priority
    }).slice(0, 25)
  }, [activeSport, getGameScores, getGameTeams, getTeamAbbr, liveEvents, liveGames, selectedGame])

  // Parse injury status
  const parseInjuryStatus = useCallback((title, description) => {
    const parts = title?.split(' - ') || []
    const playerName = parts[0] || title
    const status = parts[1] || description || 'Out'
    const statusLower = status?.toLowerCase() || ''
    
    let badge = 'OUT', badgeColor = 'bg-red-100 text-red-600', icon = '🔴'
    
    if (statusLower.includes('injured reserve') || statusLower.includes('ir')) {
      badge = 'IR'; badgeColor = 'bg-gray-200 text-gray-600'; icon = '⚫'
    } else if (statusLower.includes('day-to-day') || statusLower.includes('dtd')) {
      badge = 'DTD'; badgeColor = 'bg-amber-100 text-amber-700'; icon = '🟡'
    } else if (statusLower.includes('questionable') || statusLower.includes('gtd')) {
      badge = 'GTD'; badgeColor = 'bg-yellow-100 text-yellow-700'; icon = '🟡'
    } else if (statusLower.includes('doubtful')) {
      badge = 'DBT'; badgeColor = 'bg-orange-100 text-orange-700'; icon = '🟠'
    } else if (statusLower.includes('probable')) {
      badge = 'PROB'; badgeColor = 'bg-green-100 text-green-600'; icon = '🟢'
    } else if (statusLower.includes('cleared') || statusLower.includes('available')) {
      badge = 'OK'; badgeColor = 'bg-green-100 text-green-600'; icon = '✅'
    }
    
    return { playerName, status, badge, badgeColor, icon }
  }, [])

  // Combined updates (injuries + news)
  const updates = useMemo(() => {
    const combined = [
      ...injuries.map(i => ({ ...i, updateType: 'injury' })),
      ...news.map(n => ({ ...n, updateType: 'news' }))
    ]
    return combined.slice(0, 20)
  }, [injuries, news])

  // Segment counts
  const counts = useMemo(() => ({
    signals: edges.length, // Combined edges + line moves
    feed: liveFeed.length,
  }), [edges.length, liveFeed.length])

  // Key injuries (OUT or DTD for star players)
  const keyInjuries = useMemo(() => 
    injuries.filter(i => {
      const parsed = parseInjuryStatus(i.title, i.description)
      return parsed.badge === 'OUT' || parsed.badge === 'DTD'
    }).length
  , [injuries, parseInjuryStatus])

  // Handle item clicks
  const handleLiveItemClick = useCallback((item) => {
    const detail = item.subtitle ? `${item.title} (${item.subtitle})` : item.title
    let question = item.action || `Tell me more about ${detail}`
    
    if (item.type === 'close_game') question = `Clutch-time update on ${detail}`
    if (item.type === 'blowout') question = `Why is ${detail} a blowout?`
    if (item.source === 'backend') question = `Tell me about ${detail}`
    
    // Pass live event context including metadata for agent
    onAskAI?.({ 
      question, 
      gameId: item.gameId, 
      title: item.title, 
      type: item.type,
      liveEventContext: {
        eventType: item.type,
        title: item.title,
        subtitle: item.subtitle,
        metadata: item.metadata || null,
        source: item.source,
      }
    })
  }, [onAskAI])

  // ============================================================================
  // RENDER CONTENT SECTIONS
  // ============================================================================
  
  // Format relative time
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return ''
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h`
    return `${Math.floor(hours / 24)}d`
  }

  const gameById = useMemo(() => {
    const map = new Map()
    games.forEach(game => {
      if (game?.id != null) {
        map.set(String(game.id), game)
      }
    })
    return map
  }, [games])

  const formatPeriod = (period) => {
    if (!Number.isFinite(period) || period <= 0) return ''
    if (period === 5) return 'OT'
    if (period > 5) return `${period - 4}OT`
    return `Q${period}`
  }

  const formatPlayType = (playType) => {
    if (!playType) return ''
    const normalized = playType.toLowerCase()
    const labels = {
      td: 'Touchdown',
      fg: 'Field Goal',
      safety: 'Safety',
      interception: 'Interception',
      fumble: 'Fumble',
      sack: 'Sack',
      redzone: 'Red Zone',
      clutch: 'Clutch',
      kickoff: 'Kickoff',
      halftime: 'Halftime',
      quarter_end: 'Quarter End',
      final: 'Final',
      milestone: 'Milestone',
      big_play: 'Big Play',
      lead_change: 'Lead Change',
      tie_game: 'Tie Game',
      rushing: 'Rushing TD',
      receiving: 'Receiving TD',
      passing: 'Passing TD',
    }
    return labels[normalized] || playType
  }

  const formatNFLTitle = (item, meta, homeAbbr, awayAbbr) => {
    const playType = (meta.playType || item.type || '').toString().toLowerCase()
    const team = meta.team || ''
    const player = meta.player || ''
    const yards = meta.yards

    if (playType === 'td') {
      return team ? `${team} TD` : 'Touchdown'
    }
    if (playType === 'fg') {
      return team ? `${team} FG` : 'Field Goal'
    }
    if (playType === 'safety') {
      return team ? `${team} Safety` : 'Safety'
    }
    if (playType === 'interception') {
      return player ? `${player} INT` : 'Interception'
    }
    if (playType === 'fumble') {
      return player ? `${player} fumble lost` : 'Fumble Lost'
    }
    if (playType === 'sack') {
      return player ? `${player} sack` : 'Sack'
    }
    if (playType === 'redzone') {
      return team ? `${team} Red Zone` : 'Red Zone'
    }
    if (playType === 'lead_change') {
      return team ? `${team} takes the lead` : 'Lead change'
    }
    if (playType === 'tie_game') {
      return 'Game tied'
    }
    if (playType === 'clutch') {
      return team ? `${team} clutch score` : 'Clutch score'
    }
    if (playType === 'kickoff') {
      return homeAbbr && awayAbbr ? `Kickoff — ${awayAbbr} @ ${homeAbbr}` : 'Kickoff'
    }
    if (playType === 'halftime') {
      return 'Halftime'
    }
    if (playType === 'quarter_end') {
      return 'End of quarter'
    }
    if (playType === 'final') {
      return 'Final'
    }
    if (playType === 'milestone') {
      if (player && yards) return `${player} ${yards} yds`
      return player ? `${player} milestone` : 'Milestone'
    }
    if (playType === 'big_play') {
      if (player && yards) return `${player} ${yards}-yd play`
      return player ? `${player} big play` : 'Big play'
    }

    if (player && playType) {
      return `${player} ${formatPlayType(playType)}`
    }
    return item.title || 'Update'
  }

  // Track liked items
  const [likedItems, setLikedItems] = useState({})
  
  const handleLike = (e, itemId) => {
    e.stopPropagation()
    setLikedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }
  
  // Mock like counts (would come from backend)
  const getLikeCount = (item) => {
    const base = item.type === 'milestone' ? 12 : item.type === 'close_game' ? 8 : 3
    return likedItems[item.id] ? base + 1 : base
  }

  const renderFeedContent = () => (
    <div className="divide-y divide-gray-50">
      {liveFeed.length === 0 ? (
        <div className="text-center py-16 px-4">
          {selectedGame ? (
            <>
              <p className="text-sm text-gray-400">No events for this game yet</p>
              <p className="text-[11px] text-gray-300 mt-1">Events will appear as the game progresses</p>
              <button
                onClick={() => setSelectedGame(null)}
                className="mt-3 text-xs text-rose-500 hover:text-rose-600 font-medium"
              >
                View all games →
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400">No live events yet today</p>
              <p className="text-[11px] text-gray-300 mt-1">Events appear during games — check back when the slate starts</p>
            </>
          )}
        </div>
      ) : (
        liveFeed.map((item) => {
          const isNFL = (item.league || activeSport) === 'nfl'
          const meta = item.metadata || {}
          const game = item.gameId ? gameById.get(String(item.gameId)) : null
          const { home, away } = getGameTeams(game)
          const homeAbbr = getTeamAbbr(home)
          const awayAbbr = getTeamAbbr(away)
          const homeScore = meta.score?.home
          const awayScore = meta.score?.away
          const hasScore = Number.isFinite(homeScore) && Number.isFinite(awayScore)
          const scoreLine = hasScore
            ? (awayAbbr && homeAbbr
              ? `${awayAbbr} ${awayScore} – ${homeAbbr} ${homeScore}`
              : `${awayScore} – ${homeScore}`)
            : ''
          const periodLabel = formatPeriod(meta.period)
          const metaLine = [scoreLine, meta.clock, periodLabel].filter(Boolean).join(' • ')
          const playTypeLabel = formatPlayType(meta.playType)
          const playerLine = meta.player
            ? `${meta.player}${playTypeLabel ? ` • ${playTypeLabel}` : ''}`
            : playTypeLabel
          const driveLine = meta.driveText || (meta.downDistance && meta.yardLine != null
            ? `${meta.downDistance} at the ${meta.yardLine}`
            : meta.downDistance || '')
          const showSubtitle = !isNFL || !metaLine
          const nflTitle = isNFL ? formatNFLTitle(item, meta, homeAbbr, awayAbbr) : item.title
          const nflDetail = meta.scoringPlay?.type
            ? formatPlayType(meta.scoringPlay.type)
            : ''
          const nflSummary = [playerLine, nflDetail].filter(Boolean).join(' • ')

          return (
            <div
              key={item.id}
              className="px-4 py-2 hover:bg-gray-50 transition-colors"
            >
              {/* Main content - clickable */}
              <button
                onClick={() => handleLiveItemClick(item)}
                className="w-full text-left"
              >
                {/* Title row with timestamp */}
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] text-gray-800 flex-1">
                    <span className="font-medium">{(nflTitle || item.title)?.replace(/🔥|💨|🏀|👀|🎯/g, '').trim()}</span>
                  </p>
                  <span className="text-[10px] text-gray-300 flex-shrink-0">
                    {formatTimeAgo(item.timestamp) || '—'}
                  </span>
                </div>

                {isNFL && metaLine && (
                  <p className="text-[11px] text-gray-600 mt-0.5">{metaLine}</p>
                )}

                {isNFL && (nflSummary || playerLine) && (
                  <p className="text-[11px] text-gray-500 mt-0.5">{nflSummary || playerLine}</p>
                )}

                {isNFL && driveLine && (
                  <p className="text-[10px] text-gray-400 mt-0.5">{driveLine}</p>
                )}
                
                {showSubtitle && (
                  <p className="text-[11px] text-gray-400 mt-0.5">{item.subtitle}</p>
                )}
              </button>
            
              {/* Actions row - like button on left */}
              <div className="flex items-center justify-between mt-1">
                {/* Like button - left side */}
                <button
                  onClick={(e) => handleLike(e, item.id)}
                  className="flex items-center gap-1 group"
                >
                  <Heart 
                    size={12} 
                    className={`transition-all ${
                      likedItems[item.id] 
                        ? 'fill-rose-500 text-rose-500 scale-110' 
                        : 'text-gray-300 group-hover:text-rose-400'
                    }`}
                  />
                  <span className={`text-[10px] ${
                    likedItems[item.id] ? 'text-rose-500' : 'text-gray-300'
                  }`}>
                    {getLikeCount(item)}
                  </span>
                </button>
                
                {/* Type indicator - subtle, right side */}
                <span className="text-[9px] text-gray-300">
                  {item.type === 'close_game' && 'clutch'}
                  {item.type === 'clutch' && 'clutch'}
                  {item.type === 'milestone' && 'milestone'}
                  {item.type === 'blowout' && 'blowout'}
                  {item.type === 'prop_hit' && 'prop'}
                  {item.type === 'run' && 'run'}
                  {item.type === 'game_start' && 'tip-off'}
                  {item.type === 'halftime' && 'halftime'}
                  {item.type === 'quarter_end' && 'quarter'}
                  {item.type === 'game_end' && 'final'}
                  {item.type === 'triple_double_watch' && 'triple-dbl'}
                </span>
              </div>
            </div>
          )
        })
      )}
    </div>
  )


  // Combined signals: edges + line moves
  // Collapsible section state for Signals
  const [collapsedSections, setCollapsedSections] = useState({})
  
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  // League-agnostic signal section renderer - ALWAYS shows section header even when empty
  const renderSignalSection = (sectionId, title, IconComponent, color, items, renderItem, onItemClick, emptyMessage = 'No signals yet') => {
    const isCollapsed = collapsedSections[sectionId]
    const hasItems = items && items.length > 0
    
    return (
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <button
          onClick={() => toggleSection(sectionId)}
          className="w-full px-4 py-2.5 flex items-center justify-between bg-gray-50/80 hover:bg-gray-100/80 transition-colors"
        >
          <div className="flex items-center gap-2">
            <IconComponent size={14} className={color} />
            <span className="text-[12px] font-semibold text-gray-700">{title}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${hasItems ? 'text-gray-400 bg-gray-200' : 'text-gray-300 bg-gray-100'}`}>
              {hasItems ? items.length : '0'}
            </span>
          </div>
          {isCollapsed ? <ChevronRight size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>
        {!isCollapsed && (
          hasItems ? (
            items.map((item, idx) => (
              <button
                key={item.id || item.gameId || idx}
                onClick={() => onItemClick?.(item)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
              >
                {renderItem(item)}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-center">
              <p className="text-[11px] text-gray-400">{emptyMessage}</p>
            </div>
          )
        )}
      </div>
    )
  }

  // Rich line movement card renderer
  const renderLineMovementCard = (move) => {
    const meta = move.metadata || {}
    const marketType = meta.marketType || 'spread'
    const openValue = meta.openValue ?? move.opening?.spread ?? 0
    const currentValue = meta.currentValue ?? move.current?.spread ?? 0
    const change = meta.change ?? (currentValue - openValue)
    const direction = meta.direction || (change > 0 ? 'up' : 'down')
    const severity = meta.severity || 'notable'
    const isLive = meta.isLive || false
    const books = meta.books || move.books || []
    const openedAt = meta.openedAt || move.openingAt
    const movedAt = meta.movedAt || move.currentAt
    const matchup = meta.matchup || move.matchup || `${move.awayTeam} @ ${move.homeTeam}`
    
    // Format values based on market type
    const formatValue = (val) => {
      if (marketType === 'spread') return val > 0 ? `+${val}` : String(val)
      if (marketType === 'total') return String(val)
      return String(val)
    }
    
    // Severity colors
    const severityColors = {
      steam: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
      significant: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
      notable: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
    }
    const colors = severityColors[severity] || severityColors.notable
    
    // Market type labels
    const marketLabels = {
      spread: 'SPREAD',
      total: 'TOTAL',
      moneyline: 'ML',
    }
    
    // Format timestamps
    const formatTime = (ts) => {
      if (!ts) return null
      const date = new Date(ts)
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }
    const formatDate = (ts) => {
      if (!ts) return null
      const date = new Date(ts)
      const today = new Date()
      if (date.toDateString() === today.toDateString()) return 'Today'
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
    
    return (
      <div className={`${colors.bg} ${colors.border} border rounded-lg p-3 space-y-2`}>
        {/* Header: Matchup + Live indicator + Severity */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isLive && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-[9px] font-bold text-red-600 uppercase">Live</span>
              </span>
            )}
            <span className="text-[13px] font-semibold text-gray-900">{matchup}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${colors.badge}`}>
              {marketLabels[marketType]}
            </span>
            {severity === 'steam' && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">
                🔥 STEAM
              </span>
            )}
          </div>
        </div>
        
        {/* Main movement display */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            {/* Open value */}
            <div className="text-center">
              <p className="text-[10px] text-gray-400 uppercase">Opened</p>
              <p className="text-[15px] font-mono font-semibold text-gray-600">{formatValue(openValue)}</p>
              {openedAt && (
                <p className="text-[9px] text-gray-400">{formatDate(openedAt)} {formatTime(openedAt)}</p>
              )}
            </div>
            
            {/* Arrow */}
            <div className="flex flex-col items-center px-2">
              {direction === 'up' ? (
                <TrendingUp size={20} className="text-red-500" />
              ) : (
                <TrendingDown size={20} className="text-green-500" />
              )}
              <span className={`text-[11px] font-bold ${direction === 'up' ? 'text-red-600' : 'text-green-600'}`}>
                {change > 0 ? '+' : ''}{change.toFixed(1)}
              </span>
            </div>
            
            {/* Current value */}
            <div className="text-center">
              <p className="text-[10px] text-gray-400 uppercase">Now</p>
              <p className="text-[15px] font-mono font-bold text-gray-900">{formatValue(currentValue)}</p>
              {movedAt && (
                <p className="text-[9px] text-gray-400">{formatDate(movedAt)} {formatTime(movedAt)}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer: Books */}
        {books.length > 0 && (
          <div className="flex items-center gap-1.5 pt-1 border-t border-gray-200/50">
            <span className="text-[9px] text-gray-400">Tracked on:</span>
            {books.map((book, i) => (
              <span key={i} className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                {book === 'draftkings' ? 'DraftKings' : book === 'fanduel' ? 'FanDuel' : book}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderSignalsContent = () => {
    // Always show both sections - even when empty
    return (
      <div className="space-y-2 pb-4">
        {/* Line Movements Section - always visible */}
        {renderSignalSection(
          'lineMovers',
          'Line Movements',
          TrendingUp,
          'text-blue-500',
          lineMovements,
          renderLineMovementCard,
          (move) => onAskAI?.({ question: `What's moving the ${move.metadata?.marketType || 'line'} on ${move.metadata?.matchup || move.matchup}?`, gameId: move.metadata?.gameId || move.gameId }),
          'No significant line movements (2+ pts) detected yet'
        )}
        
        {/* Edges Section - always visible */}
        {renderSignalSection(
          'edges',
          'Edges',
          Target,
          'text-emerald-500',
          edges,
          (item) => {
            const edge = item.edge || {}
            const isTimingEdge = edge.pick?.includes('not fully priced') || edge.reasonBullets?.some(b => b.includes('barely moved'))
            const gameTitle = item.title?.replace(/🎯|🟡|👁️|Edge:|Watchlist:/g, '').trim()
            const mainSignal = edge.pick || edge.reasonBullets?.[0]?.replace(/📈|⚠️|🏥|📊|😴|❓|⚡|👀/g, '').trim() || 'Monitoring'
            
            return (
              <>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] text-gray-800 flex-1 font-medium">{gameTitle}</p>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                    edge.confidence === 'HIGH' ? 'bg-emerald-100 text-emerald-700' :
                    edge.confidence === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {isTimingEdge ? 'TIMING' : edge.confidence}
                  </span>
                </div>
                <p className="text-[12px] text-gray-600 mt-0.5 font-medium">{mainSignal}</p>
                <div className="mt-1 space-y-0.5">
                  {edge.reasonBullets?.slice(0, 2).map((bullet, i) => {
                    if (bullet.includes(mainSignal)) return null
                    const cleanBullet = bullet.replace(/📈|⚠️|🏥|📊|😴|❓|⚡|👀/g, '').trim()
                    return <p key={i} className="text-[10px] text-gray-400">{cleanBullet}</p>
                  })}
                </div>
              </>
            )
          },
          (item) => item.action && onAskAI?.(item.action),
          'Scanning for betting edges...'
        )}
      </div>
    )
  }

  // Track expanded injury cards
  const [expandedInjury, setExpandedInjury] = useState(null)

  // Group injuries using backend's 4-label system
  const groupedUpdates = useMemo(() => {
    // Status changes (type === 'status_change') - these are "just updated" 
    const statusChanges = injuries.filter(i => i.type === 'status_change')
    
    // Helper to detect long-term injuries (season-ending, surgery, etc)
    const isLongTerm = (item) => {
      const desc = (item.description || '').toLowerCase()
      const title = (item.title || '').toLowerCase()
      // Check backend flag first
      if (item.longTerm === true) return true
      // Explicit long-term keywords
      if (desc.includes('season') || desc.includes('surgery') || 
          desc.includes('indefinite') || desc.includes('months') ||
          desc.includes('weeks') || desc.includes('torn') ||
          title.includes('season') || title.includes('surgery')) {
        return true
      }
      // Injury types that are always long-term
      if (desc.includes('acl') || desc.includes('achilles')) {
        return true
      }
      return false
    }
    
    // Tonight's Impact (category === 'tonight') - EXCLUDE long-term injuries
    const tonightInjuries = injuries.filter(i => 
      i.type === 'injury' && i.category === 'tonight' && !isLongTerm(i)
    )
    
    // Split tonight by status
    const tonightOut = tonightInjuries.filter(i => {
      const status = (i.title?.split(' - ')[1] || '').toLowerCase()
      return status.includes('out') && !status.includes('gtd') && !status.includes('day-to-day')
    })
    const tonightGTD = tonightInjuries.filter(i => {
      const status = (i.title?.split(' - ')[1] || '').toLowerCase()
      return status.includes('day-to-day') || status.includes('dtd') || 
             status.includes('questionable') || status.includes('gtd') ||
             status.includes('doubtful') || status.includes('probable')
    })
    
    // Ongoing Outs - long-term injuries OR category === 'ongoing'
    // This catches Tatum etc even if backend marks them as 'tonight'
    const ongoingOuts = injuries.filter(i => 
      i.type === 'injury' && (i.category === 'ongoing' || isLongTerm(i))
    )
    
    // Recent injuries - players who just got hurt, waiting on updates
    // These have statusChangedRecently flag or were updated in last 24h
    const recentInjuries = injuries.filter(i => {
      if (i.type !== 'injury') return false
      if (isLongTerm(i)) return false // Don't show long-term as "recent"
      // Check if status changed recently (from backend flag)
      if (i.statusChangedRecently) return true
      // Or if timestamp is within last 24 hours
      const timestamp = i.timestamp ? new Date(i.timestamp).getTime() : 0
      const dayAgo = Date.now() - (24 * 60 * 60 * 1000)
      return timestamp > dayAgo && i.category !== 'tonight'
    })
    
    return { statusChanges, tonightOut, tonightGTD, ongoingOuts, recentInjuries }
  }, [injuries])

  // Render a single injury item
  const renderInjuryItem = (item) => {
    const parsed = parseInjuryStatus(item.title, item.description)
    const isExpanded = expandedInjury === item.id
    const hasGame = item.team // Could check if team plays tonight
    
    return (
      <div 
        key={item.id} 
        className="border-b border-gray-100 last:border-b-0"
      >
        <button
          onClick={() => setExpandedInjury(isExpanded ? null : item.id)}
          className="w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            {/* Status dot */}
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              parsed.badge === 'OUT' ? 'bg-red-500' : 
              parsed.badge === 'DTD' || parsed.badge === 'GTD' ? 'bg-amber-500' : 
              'bg-gray-400'
            }`} />
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-medium text-gray-800">{parsed.playerName}</span>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${parsed.badgeColor}`}>
                  {parsed.badge}
                </span>
              </div>
              <p className="text-[12px] text-gray-500 mt-0.5">
                {item.team} · {item.description || 'Injury'}
              </p>
            </div>
            {/* Expand icon */}
            {isExpanded ? (
              <ChevronUp size={16} className="text-gray-400" />
            ) : (
              <ChevronDown size={16} className="text-gray-400" />
            )}
          </div>
        </button>
        
        {/* Expanded details */}
        {isExpanded && (
          <div className="px-4 pb-3 pt-1 bg-gray-50 border-t border-gray-100">
            <div className="ml-5 space-y-2">
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-gray-500">Status</span>
                <span className="font-medium text-gray-700">{parsed.badge === 'OUT' ? 'Out tonight' : 'Game-time decision'}</span>
              </div>
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-gray-500">Injury</span>
                <span className="font-medium text-gray-700">{item.description || 'Not specified'}</span>
              </div>
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-gray-500">Team</span>
                <span className="font-medium text-gray-700">{item.team}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  // Pass full injury context to agent
                  onAskAI?.({
                    question: `What's the impact of ${parsed.playerName} being ${parsed.badge.toLowerCase()}?`,
                    injuryContext: {
                      playerName: parsed.playerName,
                      status: parsed.badge,
                      team: item.team,
                      description: item.description,
                    }
                  })
                }}
                className="w-full mt-2 py-2 text-[12px] font-medium text-rose-600 bg-rose-50 rounded-lg hover:bg-rose-100 transition"
              >
                Ask about impact →
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render a status change item (different from injury)
  const renderStatusChangeItem = (item) => {
    const isUpgrade = item.icon === '✅' || item.icon === '⬆️'
    const isDowngrade = item.icon === '⬇️'
    
    return (
      <button
        key={item.id}
        onClick={() => item.action && onAskAI?.(item.action)}
        className="w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100"
      >
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${
            isUpgrade ? 'bg-green-100' : isDowngrade ? 'bg-red-100' : 'bg-blue-100'
          }`}>
            {item.icon}
          </div>
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold text-gray-900">{item.title}</p>
            <p className="text-[12px] text-gray-500 mt-0.5">{item.description}</p>
          </div>
        </div>
      </button>
    )
  }

  // State for collapsed sections
  const [ongoingCollapsed, setOngoingCollapsed] = useState(true)
  const [clearedCollapsed, setClearedCollapsed] = useState(false)
  const [questionableCollapsed, setQuestionableCollapsed] = useState(false)
  const [outTonightCollapsed, setOutTonightCollapsed] = useState(false)
  const [newsCollapsed, setNewsCollapsed] = useState(true)
  
  // Group ongoing outs by team for better organization (must be at component level, not inside render fn)
  const ongoingByTeam = useMemo(() => {
    const grouped = {}
    groupedUpdates.ongoingOuts.forEach(item => {
      const team = item.team || 'Other'
      if (!grouped[team]) grouped[team] = []
      grouped[team].push(item)
    })
    return grouped
  }, [groupedUpdates.ongoingOuts])
  
  // Combined updates view - stacked sections
  const renderUpdatesContent = () => {
    const hasTonightOut = groupedUpdates.tonightOut.length > 0
    const hasTonightGTD = groupedUpdates.tonightGTD.length > 0
    const hasRecentInjuries = groupedUpdates.recentInjuries.length > 0
    const hasStatusChanges = groupedUpdates.statusChanges.length > 0
    const hasOngoing = groupedUpdates.ongoingOuts.length > 0
    const hasNews = news.length > 0
    const hasAnyContent = hasTonightOut || hasTonightGTD || hasRecentInjuries || hasStatusChanges || hasOngoing || hasNews
    
    return (
      <div className="space-y-3 p-3">
        {!hasAnyContent ? (
          <div className="text-center py-16 px-4">
            <p className="text-sm text-gray-400">No injury updates</p>
            <p className="text-[11px] text-gray-300 mt-1">Status changes will appear here when games are scheduled</p>
          </div>
        ) : (
          <>
            {/* 1. CLEARED - Status changes (good news) */}
            {hasStatusChanges && (
              <div>
                <button 
                  onClick={() => setClearedCollapsed(!clearedCollapsed)}
                  className="w-full px-4 py-1.5 flex items-center hover:bg-gray-50 transition-colors"
                >
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    Cleared ({groupedUpdates.statusChanges.length}) {clearedCollapsed ? '▸' : '▾'}
                  </p>
                </button>
                {!clearedCollapsed && groupedUpdates.statusChanges.map(item => (
                  <div key={item.id} className="px-4 py-2 hover:bg-gray-50 transition border-b border-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-gray-800">
                          <span className="font-medium">{item.player || item.title?.split(' - ')[0]}</span>
                          <span className="text-emerald-500 ml-1.5">✓</span>
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{item.team} · {item.description}</p>
                      </div>
                      <span className="text-[10px] text-gray-300">{formatTimeAgo(item.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 2. QUESTIONABLE - GTD */}
            {hasTonightGTD && (
              <div>
                <button 
                  onClick={() => setQuestionableCollapsed(!questionableCollapsed)}
                  className="w-full px-4 py-1.5 flex items-center hover:bg-gray-50 transition-colors"
                >
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    Questionable ({groupedUpdates.tonightGTD.length}) {questionableCollapsed ? '▸' : '▾'}
                  </p>
                </button>
                {!questionableCollapsed && groupedUpdates.tonightGTD.map(item => (
                  <div key={item.id} className="px-4 py-2 hover:bg-gray-50 transition border-b border-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-gray-800">
                          <span className="font-medium">{item.player || item.title?.split(' - ')[0]}</span>
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{item.team} · {item.description}</p>
                      </div>
                      <span className="text-[10px] text-amber-500">GTD</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* 3. OUT TONIGHT - Confirmed out */}
            {hasTonightOut && (
              <div>
                <button 
                  onClick={() => setOutTonightCollapsed(!outTonightCollapsed)}
                  className="w-full px-4 py-1.5 flex items-center hover:bg-gray-50 transition-colors"
                >
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    Out Tonight ({groupedUpdates.tonightOut.length}) {outTonightCollapsed ? '▸' : '▾'}
                  </p>
                </button>
                {!outTonightCollapsed && groupedUpdates.tonightOut.map(item => (
                  <div key={item.id} className="px-4 py-2 hover:bg-gray-50 transition border-b border-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-gray-800">
                          <span className="font-medium">{item.player || item.title?.split(' - ')[0]}</span>
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{item.team} · {item.description}</p>
                      </div>
                      <span className="text-[10px] text-red-400">out</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 4. EXTENDED - Long-term injuries */}
            {hasOngoing && (
              <div className="mt-2">
                <button 
                  onClick={() => setOngoingCollapsed(!ongoingCollapsed)}
                  className="w-full px-4 py-1.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    Extended ({groupedUpdates.ongoingOuts.length}) {ongoingCollapsed ? '▸' : '▾'}
                  </p>
                </button>
                
                {!ongoingCollapsed && (
                  <div className="px-4 py-2 space-y-2">
                    {Object.entries(ongoingByTeam).map(([team, items]) => (
                      <div key={team}>
                        <p className="text-[9px] text-gray-400 uppercase tracking-wide mb-1">{team}</p>
                        {items.map(item => (
                          <div key={item.id} className="flex items-center justify-between py-1">
                            <p className="text-[12px] text-gray-600">{item.player}</p>
                            <p className="text-[10px] text-gray-400">{item.description?.split(' - ')[0] || 'Out'}</p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 5. NEWS - minimal style */}
            {hasNews && (
              <div>
                <button 
                  onClick={() => setNewsCollapsed(!newsCollapsed)}
                  className="w-full px-4 py-1.5 flex items-center hover:bg-gray-50 transition-colors"
                >
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    News ({news.length}) {newsCollapsed ? '▸' : '▾'}
                  </p>
                </button>
                {!newsCollapsed && news.slice(0, 5).map(item => (
                  <div key={item.id} className="px-4 py-2 hover:bg-gray-50 transition border-b border-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[13px] text-gray-800 leading-snug flex-1">{item.title}</p>
                      <span className="text-[10px] text-gray-300 shrink-0">{formatTimeAgo(item.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="h-full flex flex-col bg-white">
      {/* Pulse tabs: Signals/Feed - identical to Home tabs style */}
      {mode === 'pulse_live' && (
        <div className="px-3 pb-1 flex justify-center">
          <div className="inline-flex border-b border-gray-200">
            {SEGMENTS.map((segment) => {
              const isActive = activeSegment === segment.id
              return (
                <button
                  key={segment.id}
                  onClick={() => handleSegmentChange(segment.id)}
                  className="transition-all duration-150"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: isActive ? '#FF4D6D' : '#9CA3AF',
                    borderBottom: isActive ? '2px solid #FF4D6D' : '2px solid transparent',
                    marginBottom: '-1px',
                  }}
                >
                  {segment.label}
                </button>
              )
            })}
          </div>
        </div>
      )}
      
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50/50">
        {mode === 'home_feed' ? (
          renderUpdatesContent()
        ) : (
          <>
            {activeSegment === 'signals' && renderSignalsContent()}
            {activeSegment === 'feed' && renderFeedContent()}
          </>
        )}
      </div>
    </div>
  )
}
